/**
 * <product-image-swap> — the hover/swipe image-cycling product card used in
 * the editorial grid (E-Shop grid, related products). See
 * snippets/editorial-card.liquid for the markup and
 * assets/editorial-grid.css for the styling this drives.
 *
 * A card renders two <img> elements, `.is-current` (visible) and `.is-next`
 * (hidden). Stepping loads the target image into the hidden one and only
 * then crossfades by swapping which one carries `.is-current` — so neither
 * visible element ever has its src reassigned mid-fade. Look-ahead
 * prefetching (so the *next* likely step resolves instantly) happens
 * separately, into a detached Image() rather than either visible element,
 * so it can never end up disagreeing with what's actually loaded where.
 *
 * A custom element (rather than a plain init-on-DOMContentLoaded script) so
 * this keeps working when a card is injected later — related products are
 * fetched and swapped into the page by product-recommendations.js well
 * after the initial page load.
 */
if (!customElements.get('product-image-swap')) {
  class ProductImageSwap extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      const dataScript = this.querySelector('script.editorial-grid__images-data');
      if (!dataScript) return; // zero or one image: nothing to wire up

      let images;
      try {
        images = JSON.parse(dataScript.textContent);
      } catch (error) {
        return;
      }
      if (!Array.isArray(images) || images.length < 2) return;

      this.imgA = this.querySelector('.editorial-grid__image.is-current');
      this.imgB = this.querySelector('.editorial-grid__image.is-next');
      if (!this.imgA || !this.imgB) return;

      this.initialized = true;
      this.images = images;
      this.currentIndex = 0;
      this.appliedIndex = 0; // what's actually on screen — lags currentIndex while a load is in flight
      this.arrowUsed = false; // once the visitor steps with an arrow, hover stops overriding the index
      this.loadToken = 0; // bumped on every goTo(); guards a slow fetch from a rapid earlier click winning a race

      this.preload(images[1].src); // warm the cache for the first hover/arrow step

      if (window.matchMedia('(hover: none)').matches) {
        this.classList.add('is-touch');
        this.setupTouch();
      } else {
        this.setupHover();
      }

      this.setupArrows();
    }

    setupHover() {
      // Once the visitor has stepped with an arrow, mouseenter backs off and
      // leaves the index alone — otherwise it re-asserts image 2 on top of
      // wherever the arrow just navigated to, since the pointer is still
      // over the cell after the click.
      this.addEventListener('mouseenter', () => {
        if (!this.arrowUsed) this.goTo(1);
      });
      // Same reasoning applies on the way out: once the visitor has used the
      // arrows, that position should persist until they navigate away, not
      // snap back to image 1 on every pointer exit (including a brief one
      // crossing the gap between cells). arrowUsed is intentionally never
      // cleared once set — not here, not anywhere else.
      this.addEventListener('mouseleave', () => {
        if (this.arrowUsed) return;
        this.goTo(0);
      });
    }

    setupArrows() {
      const prev = this.querySelector('.editorial-grid__arrow--prev');
      const next = this.querySelector('.editorial-grid__arrow--next');

      if (prev) {
        prev.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.arrowUsed = true;
          this.step(-1);
        });
      }

      if (next) {
        next.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.arrowUsed = true;
          this.step(1);
        });
      }
    }

    setupTouch() {
      const link = this.querySelector('.editorial-grid__media-link');
      if (!link) return;

      const threshold = 40; // px of horizontal travel before it counts as a swipe
      let startX = 0;
      let startY = 0;
      let deltaX = 0;
      let dragging = false;
      let swiped = false;

      link.addEventListener(
        'touchstart',
        (event) => {
          const touch = event.touches[0];
          startX = touch.clientX;
          startY = touch.clientY;
          deltaX = 0;
          dragging = false;
          swiped = false;
        },
        { passive: true }
      );

      link.addEventListener(
        'touchmove',
        (event) => {
          const touch = event.touches[0];
          deltaX = touch.clientX - startX;
          const deltaY = touch.clientY - startY;

          if (!dragging && Math.abs(deltaX) > 10 && Math.abs(deltaX) > Math.abs(deltaY)) {
            dragging = true;
          }
          // Once we know it's a horizontal swipe, stop the page scrolling
          // under it; a vertical drag is left alone so the page still scrolls.
          if (dragging) event.preventDefault();
        },
        { passive: false }
      );

      link.addEventListener('touchend', () => {
        if (dragging && Math.abs(deltaX) > threshold) {
          swiped = true;
          this.step(deltaX < 0 ? 1 : -1);
        }
        dragging = false;
      });

      // A swipe still ends in a click on the anchor; swallow that one click
      // so it doesn't also navigate.
      link.addEventListener('click', (event) => {
        if (swiped) {
          event.preventDefault();
          swiped = false;
        }
      });
    }

    step(direction) {
      const total = this.images.length;
      const target = (this.currentIndex + direction + total) % total;
      this.goTo(target);
    }

    // Warms the browser's cache for `url` without touching either visible
    // <img> — a later assignment of the same url to a real <img> then
    // resolves effectively instantly.
    preload(url) {
      const img = new Image();
      img.src = url;
    }

    // Loads `data` into `img` and resolves once it's actually paintable —
    // never on the mere assignment of .src — so a caller can hold the class
    // swap back until there's a decoded frame ready to show instead of a
    // blank/stale one that then pops in late and reads as a revert.
    loadImage(img, data) {
      // The .is-current element is rendered by image_tag with a srcset, and
      // srcset beats src — so the browser would keep painting the original
      // image no matter what we assign here. Drop both attributes the first
      // time we take ownership of an element; from then on this component
      // drives it with a single src.
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.src = data.src;
      img.alt = data.alt || '';
      if (img.decode) return img.decode().catch(() => {});
      if (img.complete) return Promise.resolve();
      return new Promise((resolve) => {
        img.addEventListener('load', resolve, { once: true });
        img.addEventListener('error', resolve, { once: true });
      });
    }

    goTo(targetIndex) {
      // A superseded request (see below) leaves currentIndex pointing at an
      // image that was requested but never actually applied to the DOM —
      // without also checking appliedIndex, a later goTo() back to that same
      // index would hit this guard and do nothing, leaving the cell looking
      // frozen until a second click.
      if (targetIndex === this.currentIndex && targetIndex === this.appliedIndex) return;
      const total = this.images.length;

      // Committed immediately, before anything async — this is the single
      // source of truth every other call (hover, arrows, swipe) reads, so a
      // rapid run of clicks always computes its next step from the latest
      // requested index rather than whatever has actually finished loading.
      this.currentIndex = targetIndex;
      const requestId = ++this.loadToken;

      const showing = this.imgA.classList.contains('is-current') ? this.imgA : this.imgB;
      const hidden = showing === this.imgA ? this.imgB : this.imgA;

      // Always load the target into the hidden element and swap on resolve
      // — never the currently-visible one, and never before it's ready.
      // When the url is already cached (preload(), or a normal repeat
      // visit) this resolves within the same frame, so there's no cost.
      this.loadImage(hidden, this.images[targetIndex]).then(() => {
        // Superseded by a later goTo() that fired (and resolved) before this
        // one's image finished loading — dropping it is what stops a slow,
        // now-stale fetch from clobbering a newer, already-applied step.
        if (requestId !== this.loadToken) return;

        // Jump any transition still animating on these two images to its end
        // state first, so the new one starts from a settled opacity instead
        // of stacking on top of an interrupted mid-fade.
        showing.getAnimations?.().forEach((animation) => animation.finish());
        hidden.getAnimations?.().forEach((animation) => animation.finish());

        hidden.classList.add('is-current');
        hidden.classList.remove('is-next');
        showing.classList.add('is-next');
        showing.classList.remove('is-current');
        this.appliedIndex = targetIndex;

        // Warm the cache for the likely next step — a detached Image(), so
        // it can never disagree with what's actually loaded into imgA/imgB.
        const aheadIndex = (targetIndex + 1) % total;
        if (aheadIndex !== targetIndex) this.preload(this.images[aheadIndex].src);
      });
    }
  }

  customElements.define('product-image-swap', ProductImageSwap);
}
